"use strict"
{
  const cart_drawer = document.querySelector("cart-drawer")
  const cart = document.querySelector("cart-notification") ?? cart_drawer

  // A "Sanrio product" is any cart line titled "mystery compact" (case-insensitive).
  // Once the cart holds this many, all add-to-cart / buy-now buttons are disabled.
  const SANRIO_TITLE = "mystery compact"
  const SANRIO_CART_CAP = 10

  const countSanrioItems = async () => {
    try {
      const cartData = await (await fetch("/cart.js")).json()
      const items = Array.isArray(cartData?.items) ? cartData.items : []
      return items.reduce((total, item) => {
        const title = (item?.product_title ?? "").trim().toLowerCase()
        return title.includes(SANRIO_TITLE) ? total + (item.quantity ?? 0) : total;
      }, 0)
    } catch (_) {
      return 0
    }
  }

  const applySanrioCap = (count) => {
    const buttons = document.querySelectorAll(
      'sanrio-product-form form button[type="submit"]',
    )
    const capped = count >= SANRIO_CART_CAP
    buttons.forEach((button) => {
      if (capped) {
        // Only touch buttons we haven't already capped and that aren't
        // natively disabled (e.g. sold out / unavailable).
        if (!button.dataset.sanrioCapDisabled && !button.disabled) {
          button.dataset.sanrioCapDisabled = "true"
          button.disabled = true
          button.setAttribute("aria-disabled", "true")
        }
      } else if (button.dataset.sanrioCapDisabled) {
        delete button.dataset.sanrioCapDisabled
        button.disabled = false
        button.removeAttribute("aria-disabled")
      }
    })
  }

  const refreshSanrioCap = async () => {
    applySanrioCap(await countSanrioItems())
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", refreshSanrioCap, {
      once: true,
    })
  } else {
    refreshSanrioCap()
  }

  const randomIndex = (length) => {
    if (length <= 1) {
      return 0
    }

    if (window.crypto?.getRandomValues) {
      const range = 0x100000000
      const limit = range - (range % length)
      const values = new Uint32Array(1)

      do {
        window.crypto.getRandomValues(values)
      } while (values[0] >= limit)

      return values[0] % length
    }

    return Math.floor(Math.random() * length)
  }

  const randomSuffix = () => {
    if (window.crypto?.getRandomValues) {
      const values = new Uint32Array(1)
      window.crypto.getRandomValues(values)
      return values[0].toString(36)
    }

    return Math.floor(Math.random() * 0x100000000).toString(36)
  }

  // Unbiased Fisher-Yates shuffle backed by randomIndex (crypto when available).
  const shuffle = (array) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = randomIndex(i + 1)
      const tmp = array[i]
      array[i] = array[j]
      array[j] = tmp
    }
    return array
  }

  // Shuffle-bag ("deck") randomizer: deal each id once per cycle before repeating
  // for an even distribution and no long streaks. Persists across reloads via
  // localStorage, falling back to an in-memory deck, then to a plain random pick.
  const STORAGE_PREFIX = "sanrio_random_bag:"
  const memoryBags = new Map()

  const bagKey = (ids) => STORAGE_PREFIX + [...ids].sort().join(",")

  const readBag = (key) => {
    try {
      const raw = window.localStorage?.getItem(key)
      if (!raw) return null
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : null
    } catch (_) {
      return memoryBags.get(key) ?? null
    }
  }

  const writeBag = (key, bag) => {
    memoryBags.set(key, bag)
    try {
      window.localStorage?.setItem(key, JSON.stringify(bag))
    } catch (_) {
      // localStorage unavailable; in-memory bag still holds for this session.
    }
  }

  const lastDealt = new Map()

  const dealFromBag = (ids) => {
    if (ids.length <= 1) {
      return ids[0]
    }

    const key = bagKey(ids)
    let bag = readBag(key)

    // Refill when empty or stale (eligible set changed via availability/stock).
    const idSet = new Set(ids)
    const isStale =
      !bag ||
      bag.length === 0 ||
      bag.some((id) => !idSet.has(id))

    if (isStale) {
      bag = shuffle([...ids])
      // Avoid a back-to-back repeat across cycle boundaries.
      const previous = lastDealt.get(key)
      if (bag.length > 1 && bag[bag.length - 1] === previous) {
        const swapWith = randomIndex(bag.length - 1)
        const tmp = bag[bag.length - 1]
        bag[bag.length - 1] = bag[swapWith]
        bag[swapWith] = tmp
      }
    }

    const dealt = bag.pop()
    lastDealt.set(key, dealt)
    writeBag(key, bag)
    return dealt
  }

  const parseRandomProducts = (raw) => {
    if (typeof raw !== "string") {
      return []
    }

    return raw
      .split(",")
      .filter((item) => item !== "")
      .map((item) => {
        const sep = item.lastIndexOf(":")
        if (sep === -1) {
          return { id: item, handle: null, token: item }
        }

        return {
          handle: item.slice(0, sep),
          id: item.slice(sep + 1),
          token: item,
        }
      })
  }

  const isVariantAvailable = async ({ handle, id }) => {
    if (!handle) {
      return true
    }

    try {
      const response = await fetch(`/products/${handle}.js`)
      if (!response.ok) {
        return false
      }

      const product = await response.json()
      const variant = (product.variants || []).find(
        (item) => String(item.id) === String(id),
      )
      return variant?.available === true
    } catch (_) {
      // Network hiccup: don't strip the candidate for good, let /cart/add decide.
      return true
    }
  }

  const stripUnavailableFromInputs = (candidate) => {
    document.querySelectorAll('input[name="random_products"]').forEach((input) => {
      const next = parseRandomProducts(input.value)
        .filter((item) => String(item.id) !== String(candidate.id))
        .map((item) => item.token)
        .join(",")
      input.value = next ? `${next},` : ""
    })
  }

  const isRetryableAddError = (res) => {
    if (!res?.status) {
      return false
    }

    const text = `${res.description || ""} ${res.message || ""}`.toLowerCase()
    return (
      Number(res.status) === 422 ||
      /stock|sold out|available|inventory|cannot add|no longer/.test(text)
    )
  }

  const postCartAdd = async (formBody) =>
    (
      await fetch(`${routes.cart_add_url}.js`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: formBody,
      })
    ).json()

  const addAvailableRandomProduct = async (body, items) => {
    const remaining = [...items]
    let lastDescription = ""

    while (remaining.length > 0) {
      const dealtId = dealFromBag(remaining.map((item) => item.id))
      const idx = remaining.findIndex(
        (item) => String(item.id) === String(dealtId),
      )
      const [candidate] = remaining.splice(idx < 0 ? 0 : idx, 1)

      if (!(await isVariantAvailable(candidate))) {
        stripUnavailableFromInputs(candidate)
        continue
      }

      body.set("product-id", candidate.id)
      body.set("id", candidate.id)
      body.set(
        "properties[_timestamp]",
        `${Date.now().toString()}-${randomSuffix()}`,
      )

      const response = await postCartAdd(body)
      if (!response.status) {
        return { response }
      }

      lastDescription = response.description || response.message || ""
      if (isRetryableAddError(response) && remaining.length > 0) {
        stripUnavailableFromInputs(candidate)
        continue
      }

      return { response }
    }

    return {
      exhausted: true,
      description: lastDescription,
    }
  }

  if (!customElements.get("sanrio-product-form")) {
    customElements.define(
      "sanrio-product-form",
      class SanrioProductForm extends HTMLElement {
        constructor() {
          super()
          const el = this
          if (el.dataset.touched) {
            return
          }
          el.dataset.touched = "true"
          const smartrr_form = el.offsetParent
            ? document.querySelector(".product__subscription form")
            : undefined
          const form = el.querySelector("form")
          const submitButton = el.querySelector('[type="submit"]')
          const spinner = el.querySelector(".loading-overlay__spinner")
          {
            const id_element = form?.querySelector("[name=id]")
            if (id_element) {
              id_element.disabled = false
            }
          }
          // Liquid renders randomize buttons disabled so they can't submit
          // the base product natively; enable them now that JS is running.
          if (
            submitButton?.hasAttribute("data-random-requires-js") &&
            !submitButton.hasAttribute("data-force-sold-out")
          ) {
            submitButton.disabled = false
          }
          if (cart_drawer) {
            submitButton?.setAttribute("aria-haspopup", "dialog")
          }
          const handleErrorMessage = (message = "") => {
            const errorMessageWrapper = el.querySelector(
              ".product-form__error-message-wrapper",
            )
            if (!errorMessageWrapper) return
            const errorMessage = errorMessageWrapper.querySelector(
              ".product-form__error-message",
            )
            errorMessageWrapper?.toggleAttribute("hidden", !message)
            if (errorMessage && message) {
              errorMessage.textContent = message
            }
          }
          el["handleErrorMessage"] = handleErrorMessage
          const on_submit = async (evt) => {
            evt.preventDefault()
            if (submitButton?.getAttribute("aria-disabled") === true.toString()) {
              return
            }
            handleErrorMessage()
            submitButton?.setAttribute("aria-disabled", true.toString())
            submitButton?.classList.add("loading")
            spinner?.classList.remove("hidden")
            const body = new FormData(form ?? undefined)
            // A form with random_products must never fall back to adding the
            // base product, even when every candidate was stripped as unavailable.
            const isRandomized = body.has("random_products")
            const randomItems = parseRandomProducts(body.get("random_products"))

            if (!body.get("selling_plan")) {
              const selectedSellingPlanEl = document.querySelector(
                '.yotpo-selected [name="selling_plan"]',
              )
              const sellingPlanId =
                selectedSellingPlanEl && selectedSellingPlanEl.value
                  ? selectedSellingPlanEl.value
                  : ""
              body.append("selling_plan", sellingPlanId)
            }

            if (isRandomized) {
              body.set("quantity", "1")
              body.delete("random_products")
            } else {
              const quantity = el
                .closest(".product__info-container")
                ?.querySelector("quantity-input input")
              if (quantity) {
                body.append(quantity.name, quantity.value)
              }
            }

            {
              const [not_subs] = [
                ...(smartrr_form?.querySelectorAll(
                  "div[data-smartrr-selling-plan-group-id] input",
                ) ?? []),
              ].sort(
                (a, b) =>
                  Number(a.classList.contains("show-opts")) -
                  Number(b.classList.contains("show-opts")),
              )
              if (not_subs?.checked) {
              } else {
                const select = smartrr_form?.querySelector("select")
                if (select) {
                  body.append("selling_plan", select.value)
                }
              }
            }
            if (cart) {
              body.append(
                "sections",
                cart.getSectionsToRender().map(({ id }) => id),
              )
              body.append("sections_url", location.pathname)
              cart.setActiveElement(document.activeElement)
            }
            let error = false

            try {
              let response
              if (isRandomized) {
                const result = await addAvailableRandomProduct(body, randomItems)
                if (result.exhausted || !result.response) {
                  handleErrorMessage(
                    result.description ||
                      "Sorry, this mystery item is currently unavailable.",
                  )
                  // Set before any early return so finally keeps the button disabled.
                  error = true
                  submitButton?.setAttribute("aria-disabled", true.toString())
                  const soldOutMessage =
                    submitButton?.querySelector(".sold-out-message")
                  if (!soldOutMessage) return
                  submitButton?.querySelector("span")?.classList.add("hidden")
                  soldOutMessage.classList.remove("hidden")
                } else {
                  response = result.response
                }
              } else {
                response = await postCartAdd(body)
              }

              if (!error && response?.status) {
                handleErrorMessage(response.description)
                const soldOutMessage =
                  submitButton?.querySelector(".sold-out-message")
                if (!soldOutMessage) return
                submitButton?.setAttribute("aria-disabled", true.toString())
                submitButton?.querySelector("span")?.classList.add("hidden")
                soldOutMessage.classList.remove("hidden")
                error = true
              } else if (!error && !cart) {
                location.assign(routes.cart_url ?? "/cart")
              } else if (!error) {
                error = false
                const quickAddModal = el.closest("quick-add-modal")
                if (quickAddModal) {
                  document.body.addEventListener(
                    "modalClosed",
                    () => queueMicrotask(() => cart?.renderContents(response)),
                    { once: true },
                  )
                  quickAddModal.hide(true)
                } else {
                  cart.renderContents(response)
                }
              }
            } finally {
              submitButton?.classList.remove("loading")
              cart?.classList.remove("is-empty")
              if (!error) submitButton?.removeAttribute("aria-disabled")
              spinner?.classList.add("hidden")
              // Re-evaluate the Sanrio cap so the cap-disable sticks once the
              // cart reaches the limit (and the aria-disabled reset above
              // doesn't accidentally re-enable a capped button).
              refreshSanrioCap()
            }
          }
          form?.addEventListener("submit", on_submit)
        }
      },
    )
  }
}
